import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowUpRight,
  Scale,
  ShieldCheck,
  CheckCircle2,
  Circle,
  BadgeCheck,
  HeartPulse,
  Lock,
  Landmark,
  Compass,
  Server,
} from "lucide-react";
import CircleGlow from "../components/CircleGlow";
import { vngSteps } from "../data/vngSteps";
import { leeuwardenSystems } from "../data/leeuwardenSystems";
import { loadAssessments, loadSeedOverrides } from "../lib/assessmentStore";

/**
 * /kaders & /framework — De plichten van publieke AI, in kaart gebracht.
 *
 * Two foundational legal frameworks (EU AI Act + AVG) are mapped onto the
 * VNG-lifecycle steps that satisfy them.  RiskaiΛ computes, across every
 * system in your register, how many of those VNG-step-obligations are
 * currently green — giving the gemeente an at-a-glance compliance read for
 * each law.
 */

// Applicability tiers for the Dutch municipal context (Leeuwarden).
// `mandatory`     → wettelijk verplicht voor NL/EU publieke sector
// `optional`      → internationaal supplement, aanbevolen maar niet verplicht
// `not-applicable`→ sectoraal/jurisdiction-specifiek (US-only of finance/zorg)
const APPLICABILITY = {
  MANDATORY: "mandatory",
  OPTIONAL: "optional",
  NOT_APPLICABLE: "not-applicable",
};

// VNG steps that fulfil each kader.  Drives both the obligations checklist
// shown in each card AND the progress count.
const FRAMEWORKS = [
  {
    id: "eu-ai-act",
    code: "Kader 01",
    title: "EU AI Act",
    subtitle: "Verordening 2024/1689 · Annex III · Hoog-risico AI",
    icon: Scale,
    applicability: APPLICABILITY.MANDATORY,
    summary:
      "De Europese AI-verordening eist dat publieke hoog-risico systemen een impact assessment doorlopen, traceerbaar zijn, menselijk toezicht kennen en publiek geregistreerd staan.",
    obligations: [
      {
        stepId: "ontwerp",
        label: "Impact assessment (IAMA)",
        clause: "AI Act art. 27",
      },
      {
        stepId: "ontwikkeling",
        label: "Lineage en reproduceer­baarheid",
        clause: "AI Act art. 12",
      },
      {
        stepId: "validatie",
        label: "Onafhankelijke conformiteitstoets",
        clause: "AI Act art. 43",
      },
      {
        stepId: "implementatie",
        label: "Publicatie in Algoritmeregister",
        clause: "AI Act art. 49 · NL Algoritmeregister",
      },
      {
        stepId: "monitoring",
        label: "Post-market monitoring",
        clause: "AI Act art. 72",
      },
    ],
  },
  {
    id: "avg",
    code: "Kader 02",
    title: "AVG / UK GDPR",
    subtitle: "Verordening 2016/679 · Algemene verordening gegevensbescherming",
    icon: ShieldCheck,
    applicability: APPLICABILITY.MANDATORY,
    summary:
      "De AVG verplicht een rechtsgeldige grondslag, een DPIA bij hoog risico, transparantie naar de betrokkene en een verbod op volledig geautomatiseerde besluitvorming met juridische gevolgen.",
    obligations: [
      {
        stepId: "initiatie",
        label: "Doelbinding en grondslag",
        clause: "AVG art. 5 + 6",
      },
      {
        stepId: "data-analyse",
        label: "Databron-inventaris en kwaliteit",
        clause: "AVG art. 5 lid 1 sub d",
      },
      {
        stepId: "ontwerp",
        label: "DPIA / GEB",
        clause: "AVG art. 35",
      },
      {
        stepId: "implementatie",
        label: "Recht op uitleg & menselijke tussenkomst",
        clause: "AVG art. 22",
      },
      {
        stepId: "evaluatie",
        label: "Periodieke heroverweging",
        clause: "AVG art. 24",
      },
    ],
  },
  {
    id: "bio",
    code: "Kader 03",
    title: "BIO",
    subtitle:
      "Baseline Informatiebeveiliging Overheid · verplicht voor Nederlandse overheden",
    icon: Server,
    applicability: APPLICABILITY.MANDATORY,
    summary:
      "De BIO is de bindende informatiebeveiligings-norm voor alle Nederlandse overheidsorganisaties. Zij vertaalt ISO/IEC 27001/27002 naar overheids­specifieke maatregelen rondom logging, autorisatie, leveranciersbeheer en incidentrespons — en geldt onverkort voor de algoritmes die uw gemeente in productie heeft.",
    obligations: [
      {
        stepId: "data-analyse",
        label: "Classificatie van gegevens & risico-analyse",
        clause: "BIO 5.1 · BBN",
      },
      {
        stepId: "ontwikkeling",
        label: "Logging, monitoring & toegangsbeheer",
        clause: "BIO 12.4 · 9.2",
      },
      {
        stepId: "implementatie",
        label: "Leveranciers- en ketenbeveiliging",
        clause: "BIO 15.1",
      },
      {
        stepId: "monitoring",
        label: "Incident- en kwetsbaarhedenmanagement",
        clause: "BIO 16.1 · 12.6",
      },
    ],
  },
  {
    id: "iso-42001",
    code: "Kader 04",
    title: "ISO/IEC 42001:2023",
    subtitle: "International Organization for Standardization",
    icon: BadgeCheck,
    applicability: APPLICABILITY.OPTIONAL,
    tags: ["Certificering AI-managementsysteem"],
    summary:
      "De eerste certificeerbare wereldwijde norm voor een AI-managementsysteem. Denk 'ISO 27001, maar dan voor AI'. Aanvullend op de BIO — een heldere manier om verantwoorde AI-governance internationaal aan te tonen.",
  },
  {
    id: "soc2",
    code: "Kader 05",
    title: "SOC 2 (AICPA TSC)",
    subtitle: "AICPA Trust Services Criteria · System and Organization Controls",
    icon: Lock,
    applicability: APPLICABILITY.OPTIONAL,
    tags: [
      "Beveiliging",
      "Beschikbaarheid",
      "Vertrouwelijkheid",
      "Verwerkingsintegriteit",
      "Privacy",
    ],
    summary:
      "Geen wet, maar het de-facto trust-keurmerk voor B2B-SaaS. Wordt steeds vaker gevraagd in aanbestedingen voor cloud- en AI-leveranciers — handig om mee te wegen bij inkoop.",
  },
  {
    id: "nist-ai-rmf",
    code: "Kader 06",
    title: "NIST AI Risk Management Framework 1.0",
    subtitle: "U.S. Department of Commerce · NIST AI 100-1",
    icon: Compass,
    applicability: APPLICABILITY.NOT_APPLICABLE,
    tags: ["Govern", "Map", "Measure", "Manage"],
    summary:
      "Een vrijwillige Amerikaanse standaard. Voor de Nederlandse publieke sector niet bindend — wel inhoudelijk verwant aan de EU AI Act en als naslagwerk bruikbaar.",
  },
  {
    id: "hipaa",
    code: "Kader 07",
    title: "HIPAA",
    subtitle: "Health Insurance Portability and Accountability Act · 45 CFR Parts 160 & 164",
    icon: HeartPulse,
    applicability: APPLICABILITY.NOT_APPLICABLE,
    tags: ["Privacy Rule", "Security Rule", "Breach Notification"],
    summary:
      "Amerikaanse zorgwet. Niet van toepassing op Nederlandse gemeenten — voor zorg-gerelateerde gegevens gelden AVG en sectorale Wgbo/Wabvpz, niet HIPAA.",
  },
  {
    id: "sr-11-7",
    code: "Kader 08",
    title: "SR 11-7 / EBA Modelrisico",
    subtitle: "Federal Reserve SR 11-7 · EBA/GL/2017/16 · Model Risk Management",
    icon: Landmark,
    applicability: APPLICABILITY.NOT_APPLICABLE,
    tags: ["Modelvalidatie", "Onafhankelijke toetsing"],
    summary:
      "Sectorale richtlijn voor modelrisico bij banken, verzekeraars en fintechs. Niet van toepassing op gemeentelijke AI — opgenomen voor volledigheid van het overzicht.",
  },
];

const Framework = () => {
  const [assessments, setAssessments] = useState([]);
  const [seedOverrides, setSeedOverrides] = useState({});

  useEffect(() => {
    setAssessments(loadAssessments());
    setSeedOverrides(loadSeedOverrides());
  }, []);

  // Build a flat list of {systemId, vngStatus} across all systems in the register
  const allSystems = useMemo(() => {
    const lwd = leeuwardenSystems.map((s) => {
      const lifecycleProgress = LWD_LIFECYCLE_PROGRESS[s.lifecycleStage] || [];
      const baseStatus = {};
      vngSteps.forEach((v) => {
        baseStatus[v.id] = lifecycleProgress.includes(v.id);
      });
      const overrides = seedOverrides[s.id] || {};
      return {
        id: s.id,
        name: s.name,
        vngStatus: { ...baseStatus, ...overrides },
      };
    });
    const usr = assessments
      .filter((a) => a.published)
      .map((u) => ({
        id: u.id,
        name: u.name,
        vngStatus: u.vngStatus || {},
      }));
    return [...lwd, ...usr];
  }, [assessments, seedOverrides]);

  // For each framework, compute met / total obligations across all systems
  const frameworkProgress = useMemo(() => {
    const out = {};
    FRAMEWORKS.forEach((f) => {
      if (!f.obligations) {
        out[f.id] = null;
        return;
      }
      const total = f.obligations.length * Math.max(allSystems.length, 1);
      let met = 0;
      allSystems.forEach((sys) => {
        f.obligations.forEach((o) => {
          if (sys.vngStatus[o.stepId]) met += 1;
        });
      });
      const pct =
        total === 0 ? 0 : Math.round((met / total) * 100);
      out[f.id] = { met, total, pct };
    });
    return out;
  }, [allSystems]);

  return (
    <div data-testid="page-framework">
      {/* HEADER */}
      <section className="relative overflow-hidden">
        <CircleGlow intensity={0.7} />
        <div className="rk-container relative pt-16 md:pt-24 pb-12">
          <div className="overline mb-6" data-testid="kaders-eyebrow">
            Kaders · juridische fundering van uw register
          </div>
          <h1
            className="text-white tracking-tight leading-[1.05] max-w-5xl text-5xl md:text-7xl"
            style={{ fontFamily: "Fraunces, serif", fontWeight: 300 }}
            data-testid="kaders-title"
          >
            De plichten van publieke AI,{" "}
            <em
              className="italic text-[#D4D8DF]"
              style={{ fontFamily: "Fraunces, serif" }}
            >
              in kaart gebracht.
            </em>
          </h1>
          <p
            className="mt-6 text-lg text-[#D4D8DF] max-w-3xl leading-relaxed"
            data-testid="kaders-intro"
          >
            De{" "}
            <span className="text-white">acht VNG-stappen</span> die u in de
            Cockpit afvinkt zijn niet abstract: ze vertalen de juridische
            verplichtingen uit de EU AI Act en de AVG in concrete handelingen.
            Hieronder ziet u <em>welke stap welke wet vervult</em> — en hoeveel
            verplichtingen uw register op dit moment al dekt.
          </p>
        </div>
        <div className="hairline" />
      </section>

      {/* KADER CARDS */}
      <section className="rk-container py-16">
        <div
          className="grid grid-cols-1 md:grid-cols-2 gap-px bg-[#C0C5CE]/15"
          data-testid="kaders-grid"
        >
          {FRAMEWORKS.map((f) => {
            const Icon = f.icon;
            const prog = frameworkProgress[f.id];
            const isNotApplicable =
              f.applicability === APPLICABILITY.NOT_APPLICABLE;
            const isMandatory = f.applicability === APPLICABILITY.MANDATORY;
            const isOptional = f.applicability === APPLICABILITY.OPTIONAL;
            return (
              <article
                key={f.id}
                data-testid={`kader-${f.id}`}
                data-applicability={f.applicability}
                className="bg-[#162E4A] hover:bg-[#18335A] transition-colors duration-500 p-8 md:p-10 relative"
                style={{ opacity: isNotApplicable ? 0.6 : 1 }}
              >
                {/* Applicability badge */}
                {(isMandatory || isOptional || isNotApplicable) && (
                  <div
                    className="absolute top-4 right-4 text-[9px] tracking-[0.22em] uppercase px-2.5 py-1 border"
                    data-testid={`kader-${f.id}-applicability`}
                    style={
                      isMandatory
                        ? {
                            color: "#7FD7E4",
                            borderColor: "rgba(127,215,228,0.45)",
                            background: "rgba(127,215,228,0.08)",
                          }
                        : isOptional
                        ? {
                            color: "#D4D8DF",
                            borderColor: "rgba(192,197,206,0.40)",
                          }
                        : {
                            color: "#E4A6A6",
                            borderColor: "rgba(228,166,166,0.45)",
                            background: "rgba(228,166,166,0.06)",
                          }
                    }
                  >
                    {isMandatory && "Wettelijk verplicht"}
                    {isOptional && "Aanvullend · optioneel"}
                    {isNotApplicable && "Niet van toepassing op deze sector"}
                  </div>
                )}

                {/* Top row: code · title */}
                <div className="flex items-start justify-between gap-6 mb-6 mt-6">
                  <div>
                    <div className="overline mb-2">{f.code}</div>
                    <h2
                      className="text-3xl md:text-4xl text-white tracking-tight leading-tight"
                      style={{ fontFamily: "Fraunces, serif", fontWeight: 300 }}
                    >
                      {f.title}
                    </h2>
                    <div className="mt-2 text-[11px] tracking-[0.18em] uppercase text-[#9AA4B2]">
                      {f.subtitle}
                    </div>
                  </div>
                  <div
                    className="shrink-0 w-14 h-14 border border-[#C0C5CE]/40 flex items-center justify-center"
                    aria-hidden
                  >
                    <Icon strokeWidth={1} className="w-7 h-7 text-[#C0C5CE]" />
                  </div>
                </div>

                {/* Compliance progress for this kader (only when mapped to VNG) */}
                {prog && (
                  <div
                    className="border border-[#C0C5CE]/25 px-5 py-4 mb-6"
                    data-testid={`kader-progress-${f.id}`}
                  >
                    <div className="flex items-center justify-between mb-2">
                      <span className="overline">Status van uw register</span>
                      <span
                        className="text-sm"
                        style={{ color: "#9FE2BF", fontFamily: "Fraunces, serif" }}
                      >
                        {prog.met} / {prog.total} verplichtingen voldaan
                      </span>
                    </div>
                    <div className="h-px bg-[#C0C5CE]/20 relative">
                      <div
                        className="absolute left-0 top-0 h-px"
                        style={{
                          width: `${prog.pct}%`,
                          background: "#9FE2BF",
                          boxShadow: "0 0 6px rgba(159,226,191,0.6)",
                          transition: "width 600ms ease",
                        }}
                      />
                    </div>
                    <div className="mt-2 text-[10px] tracking-[0.2em] uppercase text-[#9AA4B2]">
                      {allSystems.length} systemen · {prog.pct}%
                    </div>
                  </div>
                )}

                {/* Tag pills (for non-VNG-mapped kaders) */}
                {f.tags && (
                  <div
                    className="flex flex-wrap gap-2 mb-6"
                    data-testid={`kader-tags-${f.id}`}
                  >
                    {f.tags.map((t) => (
                      <span
                        key={t}
                        className="text-[10px] tracking-[0.18em] uppercase px-3 py-1.5 border border-[#C0C5CE]/30 text-[#D4D8DF]"
                      >
                        {t}
                      </span>
                    ))}
                  </div>
                )}

                <p className="text-[#D4D8DF] leading-relaxed mb-6">
                  {f.summary}
                </p>

                {/* Obligations mapped to VNG steps (only when mapped) */}
                {f.obligations && (
                  <>
                    <div className="hairline mb-5" />
                    <div className="overline mb-3">Plichten · gekoppeld aan VNG</div>
                    <ul className="space-y-2.5">
                      {f.obligations.map((o) => {
                        const step = vngSteps.find((s) => s.id === o.stepId);
                        // For "is this met for the gemeente?" we say yes if at
                        // least one system in the register has the step green.
                        const anyMet = allSystems.some(
                          (sys) => sys.vngStatus[o.stepId]
                        );
                        return (
                          <li
                            key={o.label}
                            className="flex items-start gap-3"
                            data-testid={`kader-${f.id}-obligation-${o.stepId}`}
                          >
                            {anyMet ? (
                              <CheckCircle2
                                strokeWidth={1.5}
                                className="w-4 h-4 mt-1 shrink-0"
                                style={{ color: "#9FE2BF" }}
                              />
                            ) : (
                              <Circle
                                strokeWidth={1}
                                className="w-4 h-4 mt-1 shrink-0 text-[#9AA4B2]"
                              />
                            )}
                            <div className="min-w-0">
                              <div className="text-sm text-white">{o.label}</div>
                              <div className="text-[11px] tracking-[0.12em] uppercase text-[#9AA4B2]">
                                {o.clause} · vervuld door VNG-stap{" "}
                                {step ? `${step.number} · ${step.title}` : o.stepId}
                              </div>
                            </div>
                          </li>
                        );
                      })}
                    </ul>
                  </>
                )}
              </article>
            );
          })}
        </div>
      </section>

      {/* CONCLUDING STATEMENT */}
      <section
        className="relative overflow-hidden"
        data-testid="kaders-conclusion"
      >
        <div className="hairline" />
        <div className="bg-[#0F2237] py-24 md:py-32 relative">
          <CircleGlow intensity={0.4} />
          <div className="rk-container relative text-center">
            <div className="overline mb-6 mx-auto">
              Het hogere doel
            </div>
            <h2
              className="text-white tracking-tight leading-[1.1] text-4xl md:text-6xl max-w-5xl mx-auto"
              style={{ fontFamily: "Fraunces, serif", fontWeight: 300 }}
              data-testid="kaders-conclusion-title"
            >
              Publiceer AI waar uw burgers{" "}
              <em
                className="italic"
                style={{
                  fontFamily: "Fraunces, serif",
                  color: "#7FD7E4",
                }}
              >
                op kunnen vertrouwen.
              </em>
            </h2>
          </div>
        </div>
        <div className="hairline" />
      </section>

      {/* CTA */}
      <section className="rk-container pb-24">
        <div className="border border-[#C0C5CE]/30 p-8 md:p-12 flex flex-col md:flex-row items-start md:items-center gap-6 md:gap-12">
          <div className="flex-1">
            <div className="overline mb-3">Van kader naar praktijk</div>
            <h3
              className="text-2xl md:text-3xl text-white tracking-tight leading-tight max-w-3xl"
              style={{ fontFamily: "Fraunces, serif", fontWeight: 300 }}
              data-testid="kaders-systems-line"
            >
              Open een systeem in de Cockpit en vink een stap af —{" "}
              <em
                className="italic text-[#D4D8DF]"
                style={{ fontFamily: "Fraunces, serif" }}
              >
                de score op deze pagina loopt mee.
              </em>
            </h3>
          </div>
          <div className="flex flex-col sm:flex-row gap-3 shrink-0">
            <Link
              to="/platform"
              className="rk-btn-primary"
              data-testid="kaders-cta-cockpit"
            >
              Open Cockpit
              <ArrowUpRight strokeWidth={1.25} className="w-4 h-4" />
            </Link>
            <Link
              to="/register"
              className="rk-btn-ghost"
              data-testid="kaders-cta-register"
            >
              Bekijk register
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

// Same lifecycle → green-steps map as SystemResult; kept local for the score sync
const LWD_LIFECYCLE_PROGRESS = {
  Initiatie: ["initiatie"],
  "Data-analyse": ["initiatie", "data-analyse"],
  Ontwerp: ["initiatie", "data-analyse", "ontwerp"],
  Ontwikkeling: ["initiatie", "data-analyse", "ontwerp", "ontwikkeling"],
  Validatie: ["initiatie", "data-analyse", "ontwerp", "ontwikkeling", "validatie"],
  Implementatie: [
    "initiatie",
    "data-analyse",
    "ontwerp",
    "ontwikkeling",
    "validatie",
    "implementatie",
  ],
  Monitoring: [
    "initiatie",
    "data-analyse",
    "ontwerp",
    "ontwikkeling",
    "validatie",
    "implementatie",
    "monitoring",
  ],
  Evaluatie: [
    "initiatie",
    "data-analyse",
    "ontwerp",
    "ontwikkeling",
    "validatie",
    "implementatie",
    "monitoring",
    "evaluatie",
  ],
};

export default Framework;
